/**
 * 단순 history API 기반 SPA 라우터.
 * Vite에서 SPA fallback을 켜면 새로고침/직접 진입도 동작.
 *
 * 보호 라우트(PROTECTED_ROUTES)에 진입하면 history에 sentinel 항목을
 * 한 칸 더 쌓아 두어 iOS Safari의 가장자리 스와이프 뒤로가기로도
 * 페이지를 떠날 수 없게 한다. popstate가 발생하면 sentinel이 소비되었다고
 * 보고 즉시 새로운 sentinel을 다시 push해 무한히 같은 라우트에 머물게 한다.
 * 사용자가 정말 나가려면 화면 내 "나가기" 버튼을 사용해야 한다.
 */

export type Route =
  | { name: 'main' }
  | { name: 'join'; prefilledCode?: string }
  | { name: 'single' }
  | { name: 'single-result' }
  | { name: 'room'; code: string }
  | { name: 'play'; code: string }
  | { name: 'result'; code: string }
  | { name: 'error'; reason?: string };

type Listener = (route: Route) => void;

const SENTINEL_KEY = '__completto_sentinel__';
type RouterHistoryState = { [SENTINEL_KEY]?: true } | null;

export class Router {
  private static readonly PROTECTED_ROUTES: ReadonlySet<Route['name']> = new Set([
    'play',
    'single',
  ]);

  private listeners = new Set<Listener>();
  private current: Route;

  constructor() {
    this.current = this.parse(window.location.pathname);
    if (Router.PROTECTED_ROUTES.has(this.current.name)) {
      this.installSentinel();
    }
    window.addEventListener('popstate', this.onPopState);
  }

  get(): Route {
    return this.current;
  }

  navigate(route: Route, replace = false): void {
    const url = this.toPath(route);
    const wasProtected = Router.PROTECTED_ROUTES.has(this.current.name);
    const isProtected = Router.PROTECTED_ROUTES.has(route.name);

    if (wasProtected && this.isOnSentinel()) {
      // sentinel 위에 머물고 있다면 sentinel을 실제 목적지로 교체.
      window.history.replaceState({}, '', url);
    } else if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }

    this.current = route;
    if (isProtected) this.installSentinel();
    this.notify();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  parse(pathname: string): Route {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (path === '/' || path === '') return { name: 'main' };
    if (path === '/join') return { name: 'join' };
    if (path === '/single') return { name: 'single' };
    if (path === '/single/result') return { name: 'single-result' };
    if (path === '/error') return { name: 'error' };
    const playMatch = path.match(/^\/room\/([A-Z0-9]+)\/play$/);
    if (playMatch?.[1]) return { name: 'play', code: playMatch[1] };
    const resultMatch = path.match(/^\/room\/([A-Z0-9]+)\/result$/);
    if (resultMatch?.[1]) return { name: 'result', code: resultMatch[1] };
    const roomMatch = path.match(/^\/room\/([A-Z0-9]+)$/);
    if (roomMatch?.[1]) return { name: 'room', code: roomMatch[1] };
    return { name: 'error', reason: 'Not Found' };
  }

  toPath(route: Route): string {
    switch (route.name) {
      case 'main':
        return '/';
      case 'join':
        return '/join';
      case 'single':
        return '/single';
      case 'single-result':
        return '/single/result';
      case 'room':
        return `/room/${route.code}`;
      case 'play':
        return `/room/${route.code}/play`;
      case 'result':
        return `/room/${route.code}/result`;
      case 'error':
        return '/error';
    }
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.current);
  }

  private installSentinel(): void {
    if (this.isOnSentinel()) return;
    window.history.pushState({ [SENTINEL_KEY]: true }, '', window.location.href);
  }

  private isOnSentinel(): boolean {
    const state = window.history.state as RouterHistoryState;
    return !!state && state[SENTINEL_KEY] === true;
  }

  private readonly onPopState = (): void => {
    if (Router.PROTECTED_ROUTES.has(this.current.name)) {
      // 보호 라우트에 머무는 동안 popstate가 오면 sentinel이 소비된 것.
      // URL을 원래 라우트로 되돌리고 sentinel을 다시 쌓는다.
      const url = this.toPath(this.current);
      if (window.location.pathname !== url) {
        window.history.replaceState({}, '', url);
      }
      this.installSentinel();
      return;
    }
    this.current = this.parse(window.location.pathname);
    this.notify();
  };
}

export const router = new Router();
